import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import {
  DataGrid,
  FilterBar,
  Button,
  MetricCard,
  METRIC_READ_CAPTIONS,
  Skeleton,
} from '@aaimplatform/ui';
import type {
  FilterDefinition,
  ActiveFilter,
  ColumnDef,
} from '@aaimplatform/ui';
import type { components as AdminPortalUiComponents } from '@aaimplatform/institutional-types/generated/admin-portal-ui';
import {
  complianceDashboardApi,
  type ComplianceDashboardAlert,
} from '../api/services/compliance-dashboard';

// ---------------------------------------------------------------------------
// Sprint C14 Lane B WP B-3: Compliance Alerts queue
//
// Surfaces compliance alerts as a single triage queue with per-row
// drill-down to the source loan. Titled 'Compliance Alerts' by the
// fi-alerts-scope ruling: Collateral Health Alert tier transitions are not
// in this feed until GH #2384 is fixed.
//
// Backing endpoint: GET /api/admin/compliance/dashboard/alerts (canonical
// in platform-admin.openapi.yaml at /compliance/dashboard/alerts). The
// brief specified `/api/admin/alerts` -- that path does NOT exist in the
// canonical platform-admin spec. The closest canonical surface that returns
// CHA / compliance alert events with severity + drill-down identifiers is
// the compliance dashboard alerts endpoint, which is what this page uses.
//
// Follow-on C14->C15-13 tracks adding a top-level `/api/admin/alerts`
// aggregator that unifies CHA breach, value-drop, and compliance event
// streams. Until then this page reads from the existing compliance alerts
// projection. ComplianceDashboardAlert carries: time, severity (LOW /
// MEDIUM / HIGH / CRITICAL), message, loanId. The drill-down link uses
// loanId when present; pledge-level drill-down requires the new aggregator.
// ---------------------------------------------------------------------------

const ALERTS_KEY = 'admin-alerts';

/** What a severity tile prints in place of a figure it never received. */
const WITHHELD_VALUE = '--';

/**
 * The table slot's wording for each outcome that produced no list. Each names
 * compliance alerts, the one feed this page reads (fi-alerts-scope).
 */
const READ_FAILED_MESSAGE = 'Compliance alerts could not be read.';
const NOT_READ_MESSAGE = 'Compliance alerts not yet read.';
const NOT_IN_RESPONSE_MESSAGE = 'Compliance alerts were not in the response.';

/**
 * The read outcome the page renders from, derived from the query result the
 * way ComplianceStatus.tsx derives it.
 *
 * The page tells four outcomes apart. TanStack v5 defines `isLoading` as
 * `isPending && isFetching`, so a first fetch that is paused (the browser
 * offline before this page mounts, networkMode default) is pending with
 * isFetching false: past the skeleton branch, and neither error nor success.
 * No response exists in that state, so nothing may be said about one. The
 * four outcomes are QueryReadOutcome in schema-registry/admin-portal-ui.openapi.yaml.
 */
type AlertsReadOutcome = AdminPortalUiComponents['schemas']['QueryReadOutcome'];

function readOutcome(query: {
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}): AlertsReadOutcome {
  if (query.isError) return 'failed';
  if (query.isLoading) return 'loading';
  if (query.isSuccess) return 'read';
  return 'unread';
}

/** Why the severity tiles withheld their figures, for each outcome without a list. */
const WITHHELD_CAPTION: Record<Exclude<AlertsReadOutcome, 'loading'>, string> = {
  failed: METRIC_READ_CAPTIONS.failed,
  unread: METRIC_READ_CAPTIONS.unread,
  read: METRIC_READ_CAPTIONS.notReported,
};

/** The table slot's sentence for each outcome without a list. */
const WITHHELD_MESSAGE: Record<Exclude<AlertsReadOutcome, 'loading'>, string> = {
  failed: READ_FAILED_MESSAGE,
  unread: NOT_READ_MESSAGE,
  read: NOT_IN_RESPONSE_MESSAGE,
};

// ---------------------------------------------------------------------------
// Window options for the alerts feed
// ---------------------------------------------------------------------------

const WINDOW_OPTIONS = [
  { value: '24', label: 'Last 24 hours' },
  { value: '72', label: 'Last 3 days' },
  { value: '168', label: 'Last 7 days' },
];

/** The read window with no Window chip active. */
const DEFAULT_WINDOW_HOURS = 24;

/** The window the page read, as a phrase ("last 24 hours"). */
function windowPhrase(hours: number): string {
  const option = WINDOW_OPTIONS.find((o) => o.value === String(hours));
  return option ? option.label.toLowerCase() : `last ${hours} hours`;
}

/**
 * What an empty alerts grid says. The grid's default read "No data available"
 * and never said that the queue covers one window (fi-g1-5, fi-g1-11). A
 * severity chip or search that matches nothing is not an empty window.
 */
export function alertsEmptyMessage({
  hours,
  narrowed,
}: {
  hours: number;
  narrowed: boolean;
}): string {
  const span = windowPhrase(hours);
  if (narrowed) return `No compliance alerts match this severity or search in the ${span}.`;
  return `No compliance rule violations in the ${span}.`;
}

// ---------------------------------------------------------------------------
// Severity badge
// ---------------------------------------------------------------------------

/** Severity labels, keyed by the generated ComplianceDashboardAlert enum. */
const SEVERITY_LABEL: Record<NonNullable<ComplianceDashboardAlert['severity']>, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

/**
 * A row whose severity field is absent says so; a value outside the enum
 * prints "Unrecognized" with the received code in its title. Neither prints a
 * raw code.
 */
function severityLabel(severity: string | undefined): string {
  if (!severity) return METRIC_READ_CAPTIONS.notReported;
  return Object.prototype.hasOwnProperty.call(SEVERITY_LABEL, severity)
    ? SEVERITY_LABEL[severity as NonNullable<ComplianceDashboardAlert['severity']>]
    : 'Unrecognized';
}

/**
 * Badge classes per severity. The preset defines no `info` color and no bare
 * `neutral` color, so the `bg-info/10 text-info` and `bg-neutral/10` this used
 * for LOW and for an absent severity generated no CSS (op-g1-8). Every class
 * here is a preset token.
 */
export function getSeverityClasses(severity: string | undefined): string {
  switch (severity) {
    case 'CRITICAL':
      return 'bg-error/20 text-error';
    case 'HIGH':
      return 'bg-error/10 text-error';
    case 'MEDIUM':
      return 'bg-warning/10 text-warning';
    case 'LOW':
      return 'bg-recessed text-primary';
    default:
      return 'bg-recessed text-secondary';
  }
}

function SeverityBadge({ severity }: { severity: string | undefined }) {
  return (
    <span
      className={`inline-flex px-3 py-1 text-xs font-sans font-semibold rounded-sm leading-normal ${getSeverityClasses(severity)}`}
      title={severity && severityLabel(severity) === 'Unrecognized' ? severity : undefined}
    >
      {severityLabel(severity)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Time formatter
// ---------------------------------------------------------------------------

function formatTime(value: string | undefined): string {
  if (!value) return '--';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

function useColumns(): ColumnDef<ComplianceDashboardAlert, unknown>[] {
  const navigate = useNavigate();
  return useMemo<ColumnDef<ComplianceDashboardAlert, unknown>[]>(
    () => [
      {
        accessorKey: 'time',
        header: 'Time',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatTime(getValue() as string | undefined)}
          </span>
        ),
        size: 200,
      },
      {
        accessorKey: 'severity',
        header: 'Severity',
        cell: ({ getValue }) => (
          <SeverityBadge severity={getValue() as string | undefined} />
        ),
        size: 120,
      },
      {
        accessorKey: 'message',
        header: 'Event',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-primary leading-normal">
            {(getValue() as string | undefined) ?? '--'}
          </span>
        ),
        size: 480,
      },
      {
        accessorKey: 'loanId',
        header: 'Source',
        cell: ({ getValue }) => {
          const loanId = getValue() as string | undefined;
          if (!loanId) {
            return (
              <span className="text-xs font-sans text-tertiary leading-normal">
                --
              </span>
            );
          }
          return (
            <span className="text-xs font-mono text-tertiary leading-normal tabular-nums">
              {loanId}
            </span>
          );
        },
        size: 200,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const loanId = row.original.loanId;
          if (!loanId) {
            return (
              <span className="text-xs font-sans text-tertiary leading-normal">
                No drill-down
              </span>
            );
          }
          return (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // Drill-down routes through the loan record. When the
                // C14->C15-13 follow-on lands a top-level alerts aggregator
                // that carries pledgeId / collateralPackageId, this should
                // route to /pledges/:pledgeId instead.
                void navigate(`/loans/active?loanId=${encodeURIComponent(loanId)}`);
              }}
            >
              View source
            </Button>
          );
        },
        size: 140,
      },
    ],
    [navigate],
  );
}

// ---------------------------------------------------------------------------
// FilterBar
// ---------------------------------------------------------------------------

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'severity',
    label: 'Severity',
    type: 'select',
    options: (['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const).map((value) => ({
      value,
      label: SEVERITY_LABEL[value],
    })),
  },
  {
    id: 'window',
    label: 'Window',
    type: 'select',
    options: WINDOW_OPTIONS,
  },
];

// ---------------------------------------------------------------------------
// AlertsPage
// ---------------------------------------------------------------------------

export function AlertsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const columns = useColumns();

  const windowChip = activeFilters.find((f) => f.filterId === 'window')?.value;
  const severityChip = activeFilters.find((f) => f.filterId === 'severity')?.value;

  const hours = windowChip ? parseInt(windowChip, 10) : DEFAULT_WINDOW_HOURS;

  const alertsQuery = useQuery({
    queryKey: [ALERTS_KEY, hours],
    queryFn: () => complianceDashboardApi.getAlerts(hours),
  });
  const { data, error } = alertsQuery;
  const outcome = readOutcome(alertsQuery);

  // The list is a read's OUTPUT, so it exists only where a read produced one.
  //
  // GH #2917 deliverable 3. `alerts` is OPTIONAL on the generated 200 type, so
  // a 200 that omits it is contract-valid and says nothing about this
  // institution's alert queue. The empty-array coalesce that stood here
  // answered `[]` on the server's behalf, and the page then drew an omitted
  // list as a measured-empty table with a measured-zero severity rollup beside
  // it. The body now reaches this page as it was read, and the query type is
  // inferred from the service so the optionality survives the whole way.
  //
  // `undefined` arrives for three separate reasons, and the wordings keyed by
  // `outcome` keep them apart: the read rejected (`failed`), the read never ran
  // (`unread`, a paused first fetch), or the read succeeded and its body carried
  // no list (`read`). An `[]` is a measurement and keeps the empty state it
  // already had.
  //
  // The list binds through `Array.isArray`. A JSON `null` in place of the list,
  // or any other non-array, is a list nobody received, and a presence test would
  // hand `.filter` a value that has none.
  //
  // `data` binds whatever the outcome. After a background refetch rejects,
  // TanStack keeps the prior payload and reports error, so the rows a previous
  // read produced stay on screen beneath the failure notice. Whether they should
  // is an open operator decision (GH #2917 deliverable 4), and this binding
  // leaves that behavior as it stood.
  const listed = data?.alerts;
  const alerts: ComplianceDashboardAlert[] | undefined = Array.isArray(listed)
    ? listed
    : undefined;

  const filteredAlerts = useMemo<ComplianceDashboardAlert[] | undefined>(() => {
    if (alerts === undefined) return undefined;
    let rows = alerts;
    if (severityChip) {
      rows = rows.filter((a) => a.severity === severityChip);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      rows = rows.filter(
        (a) =>
          (a.message ?? '').toLowerCase().includes(query) ||
          (a.loanId ?? '').toLowerCase().includes(query),
      );
    }
    return rows;
  }, [alerts, severityChip, searchQuery]);

  // The rollup counts the rows a read produced. With no list in the response
  // there is nothing to count, so every tile withholds its figure and the
  // caption beside it says why. A measured-empty list still counts, and each
  // tile prints a 0 it earned.
  const severityCounts = useMemo(() => {
    if (filteredAlerts === undefined) return undefined;
    return {
      critical: filteredAlerts.filter((a) => a.severity === 'CRITICAL').length,
      high: filteredAlerts.filter((a) => a.severity === 'HIGH').length,
      medium: filteredAlerts.filter((a) => a.severity === 'MEDIUM').length,
      low: filteredAlerts.filter((a) => a.severity === 'LOW').length,
    };
  }, [filteredAlerts]);

  // Why a withheld figure was withheld, or undefined once the figures printed.
  // It rides MetricCard's `caption` slot, which carries no change color and no
  // monospace, so a withheld read is never dressed as a measured movement.
  const rollupCaption =
    severityCounts !== undefined || outcome === 'loading'
      ? undefined
      : WITHHELD_CAPTION[outcome];

  return (
    <div>
      {/*
        What this queue reads, stated where the FI reads it, under the
        'Compliance Alerts' title. The feed is the compliance dashboard's
        violation rows; a Collateral Health Alert (a pledge's CHA tier) is not
        in it, so a RED pledge on Active Pledges and a zero here are both true
        at once. Without this line the page reads as the institution's whole
        alert picture.

        fi-alerts-scope (operator ruling): the page is titled for what it holds
        now, and ORANGE and RED tier transitions join the queue once GH #2384
        (the tier computation reads the advance rate, not collateral quality)
        is fixed. The issue number stays in this comment and out of the FI's
        copy.
      */}
      <p className="text-sm font-sans text-tertiary mb-4">
        This queue lists compliance alerts: compliance rule violations from the{' '}
        {windowPhrase(hours)}; the counts below cover the same window. Collateral
        Health Alerts for pledged collateral are shown by tier on{' '}
        <Link to="/pledges/active" className="text-accent hover:underline">
          Active Pledges
        </Link>
        . Orange and Red tier transitions are not in this queue yet; they are
        planned to join it.
      </p>

      {error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load alerts: {error.message}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {outcome === 'loading' ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-surface p-6 rounded-sm shadow-sm">
              <Skeleton className="h-10 w-12 mb-3" />
              <Skeleton className="h-3 w-24 mb-2" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))
        ) : (
          <>
            <MetricCard
              label="Critical"
              value={
                severityCounts === undefined
                  ? WITHHELD_VALUE
                  : String(severityCounts.critical)
              }
              caption={rollupCaption}
              change={
                severityCounts === undefined
                  ? undefined
                  : severityCounts.critical > 0
                    ? 'Immediate action'
                    : 'None critical'
              }
              /* None critical is an absence, not a gain: neutral, never green. */
              changeType={
                severityCounts !== undefined && severityCounts.critical > 0
                  ? 'negative'
                  : 'neutral'
              }
            />
            <MetricCard
              label="High"
              value={
                severityCounts === undefined
                  ? WITHHELD_VALUE
                  : String(severityCounts.high)
              }
              caption={rollupCaption}
              change={
                severityCounts === undefined
                  ? undefined
                  : severityCounts.high > 0
                    ? 'Review needed'
                    : 'None high'
              }
              changeType={
                severityCounts !== undefined && severityCounts.high > 0
                  ? 'negative'
                  : 'neutral'
              }
            />
            <MetricCard
              label="Medium"
              value={
                severityCounts === undefined
                  ? WITHHELD_VALUE
                  : String(severityCounts.medium)
              }
              caption={rollupCaption}
              change={severityCounts === undefined ? undefined : 'Monitor'}
              changeType="neutral"
            />
            <MetricCard
              label="Low"
              value={
                severityCounts === undefined
                  ? WITHHELD_VALUE
                  : String(severityCounts.low)
              }
              caption={rollupCaption}
              change={severityCounts === undefined ? undefined : 'Informational'}
              changeType="neutral"
            />
          </>
        )}
      </div>

      <FilterBar
        filters={FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search alerts"
        className="rounded-t-sm"
      />

      {outcome === 'loading' ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : filteredAlerts === undefined ? (
        /*
          The queue is withheld where no list was read, and the sentence takes
          the grid's own slot so the rollup and the filter chrome above it stay
          rendered through every outcome (the GH #2924 shape). A grid drawn over
          a list nobody received is the claim this branch exists to withhold.
        */
        <div className="bg-surface rounded-b-sm shadow-sm p-6">
          <p className="py-12 text-center text-sm font-sans text-tertiary">
            {WITHHELD_MESSAGE[outcome]}
          </p>
        </div>
      ) : (
        <DataGrid<ComplianceDashboardAlert>
          data={filteredAlerts}
          columns={columns}
          enablePagination
          defaultPageSize={20}
          enableColumnResize
          hoverable
          striped
          getRowId={(row) => `${row.time ?? ''}-${row.loanId ?? ''}-${row.message ?? ''}`}
          emptyMessage={alertsEmptyMessage({
            hours,
            narrowed: Boolean(severityChip) || searchQuery.trim() !== '',
          })}
          className="w-full rounded-b-sm shadow-sm"
        />
      )}
    </div>
  );
}
